import {
    activeAngle,
    angleIndexOf,
    angleLabel,
    angleSourceTime,
    audioAngle,
    audioAngleIndexOf,
    clipVolume,
    getLayerByKind,
    hasCustomVolume,
    isClipMuted,
    insetSoundFrom,
    isImageAssetType,
    pipParams,
    toSafeNumber,
} from '../state/timelineModel';
import { canEmitEffect, emitEffect } from './emitEffect';

/**
 * Compile the timeline into edit-script DSL.
 *
 * The script — not the graph — is what gets sent: the backend compiles it with
 * the same compiler an AI plan is validated by, so a hand-made edit executes on
 * exactly the same rails as a generated one, and an impossible timeline fails
 * with a real error instead of a broken render.
 *
 * Throws an Error whose message is safe to show the user.
 */

// The DSL has no string escapes, so a quote inside text would end the argument.
const toScriptString = (value) => `"${String(value || '').replace(/["\\\n\r]/g, ' ').trim()}"`;

const formatScriptSeconds = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) return '0';
    return Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
};

/** A volume multiplier, where 0 is a real value rather than "unset". */
const formatScriptLevel = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) return '0';
    return Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
};

/**
 * Does this clip play less than the whole source file?
 *
 * `sourceIn > 0` is the case the old emitter got wrong: it only ever wrote
 * trim(clip, 0, duration), so dragging the left handle moved the in-point on
 * screen and then threw it away at render time. The `trimmed` flag is still
 * honoured for clips authored before in/out points existed, where it is the
 * only record that a right-edge drag happened.
 */
const isTrimmed = (item) => (toSafeNumber(item?.sourceIn) ?? 0) > 0 || Boolean(item?.trimmed);

export const buildScriptFromTimeline = (graph, availableAssets = []) => {
    const assetByFileName = new Map();
    (Array.isArray(availableAssets) ? availableAssets : []).forEach((asset) => {
        if (asset?.FileName) assetByFileName.set(String(asset.FileName), asset);
    });

    const layerItems = (kind, fallbackName) => {
        const layer = getLayerByKind(graph, kind, fallbackName);
        const items = Array.isArray(layer?.items) ? [...layer.items] : [];
        return items.sort((a, b) => (toSafeNumber(a?.start) || 0) - (toSafeNumber(b?.start) || 0));
    };

    const videoItems = layerItems('video', 'layer 1');
    const audioItems = layerItems('audio', 'layer 2');
    const effectItems = layerItems('effects', 'layer 0');
    const overlayItems = layerItems('overlay', 'overlay');

    // Legacy AI-planned text, which carries text_content rather than params.
    const textItems = effectItems.filter(item => !item.type && String(item?.text_content || '').trim());

    /* An effect bound to a clip is applied to that clip's own stream before the
       concat, so it travels with the clip when it is moved or reordered. One
       with no clip — every effect an AI plan produces — is a step over the
       whole chain and goes on after the concat, exactly where it runs today. */
    const emittableEffects = effectItems.filter(canEmitEffect);
    const effectsByClipId = new Map();
    const timelineWideEffects = [];
    emittableEffects.forEach((effect) => {
        if (!effect.clipId) {
            timelineWideEffects.push(effect);
            return;
        }
        if (!effectsByClipId.has(effect.clipId)) effectsByClipId.set(effect.clipId, []);
        effectsByClipId.get(effect.clipId).push(effect);
    });

    if (videoItems.length === 0) {
        throw new Error('Add at least one clip to the video layer before rendering.');
    }

    const lines = [];
    const openFile = (fileName, variable, stillDuration) => {
        if (!fileName) {
            throw new Error('A clip on the timeline is not linked to an asset. Remove it and add it again.');
        }
        if (!assetByFileName.has(fileName)) {
            throw new Error(`"${fileName}" is no longer in this project's media.`);
        }

        // Stills have no intrinsic length, so the compiler demands one.
        const needsDuration = isImageAssetType(assetByFileName.get(fileName)?.FileType);
        lines.push(needsDuration
            ? `${variable} = Open(${toScriptString(fileName)}, ${formatScriptSeconds(stillDuration || 4)}s)`
            : `${variable} = Open(${toScriptString(fileName)})`);
        return variable;
    };

    /* A multicam segment is read through whichever camera is live on it, so the
       file it opens is that angle's, not whatever the clip was first made
       from. The two normally agree -- the store keeps asset_filename pointing
       at the live angle -- and when they do not, the angle wins. */
    const sourceFileOf = (item) => {
        const live = activeAngle(item);
        return String(live?.asset_filename || item?.asset_filename || item?.filename || '').trim();
    };

    const openItem = (item, prefix, index) => openFile(
        sourceFileOf(item), `${prefix}${index + 1}`, toSafeNumber(item?.duration)
    );

    const angleNameOf = (angle, index) => {
        const asset = assetByFileName.get(String(angle?.asset_filename || ''));
        return asset?.FileAlias || angle?.asset_filename || angleLabel(angle, index);
    };

    /**
     * This segment's master span, expressed in one angle's own timeline.
     *
     * A camera that started late simply has no footage at the head of the take,
     * and a span reaching back before its first frame is not something the
     * renderer can make sense of. Saying which camera and by how much is the
     * difference between fixing an offset and staring at a failed render.
     */
    const angleSpanOf = (item, angle, index) => {
        const sourceIn = toSafeNumber(item?.sourceIn) ?? 0;
        const duration = toSafeNumber(item?.duration);
        const sourceOut = toSafeNumber(item?.sourceOut)
            ?? (duration !== null ? sourceIn + duration : sourceIn);
        const from = angleSourceTime(angle, sourceIn);
        const to = angleSourceTime(angle, sourceOut);
        if (to <= from) {
            throw new Error(`${angleNameOf(angle, index)} has no footage for one of the cuts.`);
        }
        if (from < -1e-6) {
            throw new Error(
                `${angleNameOf(angle, index)} was not rolling yet ${Math.abs(from).toFixed(2)}s `
                + 'before its first frame. Move the cut later, or correct that angle\'s offset.'
            );
        }
        return { from: Math.max(0, from), to };
    };

    /**
     * Say how loud this clip is, when that differs from its source.
     *
     * A mute is emitted as get_video() rather than volume(clip, 0) on purpose:
     * dropping the audio half works on anything, while setting the volume of a
     * source that turns out to have no audio track is an error on the render
     * side, and a mute must never be the step that fails a render. Any other
     * level does need volume(), and does need the clip to have sound — the
     * renderer says so plainly if it does not.
     */
    const emitVolume = (item, variable) => {
        if (!hasCustomVolume(item)) return variable;
        const next = `${variable}v`;
        lines.push(isClipMuted(item)
            ? `${next} = get_video(${variable})`
            : `${next} = volume(${variable}, ${formatScriptLevel(clipVolume(item))})`);
        return next;
    };

    const videoStreams = videoItems.map((item, index) => {
        let variable = openItem(item, 'v', index);
        const live = activeAngle(item);

        if (live) {
            /* A multicam segment always states its span, even one starting at
               zero: the master clock and this camera's own are offset, so "no
               trim" would mean "from this camera's first frame", which is a
               different moment of the take.

               The sound is taken from the designated angle for THIS segment's
               span and paired with the picture here, rather than laid under the
               finished sequence as one continuous bed. A bed only stays in sync
               while the segments remain contiguous and in master order; cut one
               out or reorder two and the sound drifts against the picture,
               silently. Per segment it cannot. */
            const liveIndex = angleIndexOf(item);
            const span = angleSpanOf(item, live, liveIndex);
            const trimmed = `${variable}t`;
            lines.push(`${trimmed} = trim(${variable}, ${formatScriptSeconds(span.from)}, ${formatScriptSeconds(span.to)})`);
            variable = trimmed;

            const soundIndex = audioAngleIndexOf(item);
            // When the live camera is also the one carrying the sound, its own
            // audio is already travelling with the picture through the trim.
            if (soundIndex !== liveIndex) {
                const sound = audioAngle(item);
                const soundSpan = angleSpanOf(item, sound, soundIndex);
                const soundVar = `m${index + 1}`;
                openFile(String(sound.asset_filename || ''), soundVar, null);
                lines.push(`${soundVar}t = trim(${soundVar}, ${formatScriptSeconds(soundSpan.from)}, ${formatScriptSeconds(soundSpan.to)})`);
                lines.push(`${soundVar}a = get_audio(${soundVar}t)`);
                const paired = `${variable}s`;
                lines.push(`${paired} = add_audio(${variable}, ${soundVar}a)`);
                variable = paired;
            }
        } else if (isTrimmed(item)) {
            const sourceIn = toSafeNumber(item?.sourceIn) ?? 0;
            const duration = toSafeNumber(item?.duration);
            const sourceOut = toSafeNumber(item?.sourceOut) ?? (duration !== null ? sourceIn + duration : null);
            if (sourceOut !== null && sourceOut > sourceIn) {
                const trimmed = `${variable}t`;
                lines.push(`${trimmed} = trim(${variable}, ${formatScriptSeconds(sourceIn)}, ${formatScriptSeconds(sourceOut)})`);
                variable = trimmed;
            }
        }

        // A still has no soundtrack to scale, so nothing is said about its.
        if (!isImageAssetType(assetByFileName.get(String(item?.asset_filename || item?.filename || ''))?.FileType)) {
            variable = emitVolume(item, variable);
        }

        (effectsByClipId.get(item.id) || []).forEach((effect, effectIndex) => {
            const next = `${variable}f${effectIndex + 1}`;
            const line = emitEffect(effect, variable, next, item);
            if (line) {
                lines.push(line);
                variable = next;
            }
        });

        return variable;
    });

    let current = videoStreams[0];
    if (videoStreams.length > 1) {
        lines.push(`vcat = concat(${videoStreams.join(', ')})`);
        current = 'vcat';
    }

    timelineWideEffects.forEach((effect, index) => {
        const next = `vfx${index + 1}`;
        const line = emitEffect(effect, current, next, null);
        if (line) {
            lines.push(line);
            current = next;
        }
    });

    textItems.forEach((item, index) => {
        const next = `vtext${index + 1}`;
        const x = toSafeNumber(item?.text_x);
        const y = toSafeNumber(item?.text_y);
        lines.push(`${next} = drawtext(${current}, ${toScriptString(item.text_content)}, x=${x === null ? 50 : Math.round(x)}, y=${y === null ? 50 : Math.round(y)})`);
        current = next;
    });

    /* Insets go over the finished main sequence, at timeline times, so one can
       run across any number of cuts underneath. The render ends with the main
       sequence, so an inset is cut there -- and one starting after it is not
       emitted at all; the timeline hatches both so neither comes as a surprise.

       An inset's sound, when it has any, is read through a second Open of the
       same file rather than from the stream pip() consumed: a stream feeding
       two steps would need a split, and ffmpeg-python merges the identical
       inputs back into one anyway. */
    const mainEnd = videoItems.reduce((end, item) => Math.max(end, toSafeNumber(item?.end) ?? 0), 0);
    const insetSounds = [];

    overlayItems.forEach((item, index) => {
        const start = toSafeNumber(item?.start) ?? 0;
        if (start >= mainEnd) return;
        const end = Math.min(toSafeNumber(item?.end) ?? mainEnd, mainEnd);
        const sourceIn = toSafeNumber(item?.sourceIn) ?? 0;
        const sourceOut = sourceIn + (end - start);
        const cut = sourceIn > 0 || end < (toSafeNumber(item?.end) ?? end) || Boolean(item?.trimmed);

        const openInset = (prefix) => {
            const variable = openFile(sourceFileOf(item), `${prefix}${index + 1}`, null);
            if (!cut) return variable;
            lines.push(`${variable}t = trim(${variable}, ${formatScriptSeconds(sourceIn)}, ${formatScriptSeconds(sourceOut)})`);
            return `${variable}t`;
        };

        const picture = openInset('o');
        const { x, y, scale } = pipParams(item);
        const next = `vpip${index + 1}`;
        lines.push(`${next} = pip(${current}, ${picture}, x=${formatScriptLevel(x)}, y=${formatScriptLevel(y)}, `
            + `scale=${formatScriptLevel(scale)}, start=${formatScriptSeconds(start)}s, end=${formatScriptSeconds(end)}s)`);
        current = next;

        /* An inset turned all the way down is heard as nothing, and "the inset
           alone" at zero would silence the whole span -- so a zero level falls
           back to the main's sound, which is what the viewer would expect. */
        const soundFrom = insetSoundFrom(item);
        if (soundFrom !== 'main' && !isClipMuted(item)) {
            insetSounds.push({ item, index, start, end, solo: soundFrom === 'inset', openInset });
        }
    });

    // A muted music bed is a music bed that is not there: it is left out of the
    // mix entirely rather than mixed in at zero, which renders the same and
    // costs a filter less.
    const audibleAudioItems = audioItems.filter(item => !isClipMuted(item));

    if (audibleAudioItems.length > 0) {
        const audioStreams = audibleAudioItems.map((item, index) => (
            emitVolume(item, openItem(item, 'a', index))
        ));
        let audioStream = audioStreams[0];
        if (audioStreams.length > 1) {
            lines.push(`amixed = amix(${audioStreams.join(', ')})`);
            audioStream = 'amixed';
        }
        lines.push(`vaudio = add_audio(${current}, ${audioStream})`);
        current = 'vaudio';
    }

    // After the bed, not before: add_audio replaces the soundtrack it is given,
    // so an inset mixed in earlier would be silently thrown away by the music.
    // "The inset alone" silences the bed as well as the clips' own sound, since
    // by this point the bed is the soundtrack.
    insetSounds.forEach(({ item, index, start, end, solo, openInset }) => {
        const sound = emitVolume(item, openInset('os'));
        const next = `vmix${index + 1}`;
        lines.push(`${next} = mix_audio(${current}, ${sound}, at=${formatScriptSeconds(start)}s, `
            + `end=${formatScriptSeconds(end)}s${solo ? ', solo=true' : ''})`);
        current = next;
    });

    // A lone untouched clip compiles to a plan with no steps, which the
    // executor rejects as "No steps generated". Say what is missing here
    // rather than letting it fail three layers down.
    const hasOperation = lines.some(line => !/^\w+ = Open\(/.test(line));
    if (!hasOperation) {
        throw new Error('This timeline is one clip with no changes. Add another clip, trim it, or add text or audio first.');
    }

    lines.push(`Save(${current})`);
    return lines.join('\n');
};

export default buildScriptFromTimeline;
